import ResizeObserver from "resize-observer-polyfill";
import { createPopper } from "@popperjs/core";
import { useEffect, useMemo, useRef } from "react";
import { createVirtualElement, registerVirtualInstance } from "./virtualElement";
import { ARROW_SIZE } from "./PopperBox.styled";
export const usePopperJs = ({ hasArrow, isOpen, isTouch, overlayOffset, overlayRef, positionerRef, placement, shouldFollowCursor, fallbackPlacements: fallbackPlacementsProp, targetElement, placementRef }) => {
    const popperRef = useRef(null);
    /* Memoize arrays to prevent effects from re-running */
    const fallbackPlacementString = fallbackPlacementsProp?.toString();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const fallbackPlacements = useMemo(() => fallbackPlacementsProp, [fallbackPlacementString]);
    // Manage setting initial coordinates for popper placement
    const initialCoordinatesRef = useRef({ x: 0, y: 0 });
    const initialEvent = isTouch ? "touchstart" : "mouseenter";
    // Update on move while closed, so in the right position when opened
    useEffect(() => {
        if (process.env.NODE_ENV === "test") {
            return;
        }
        let targetDomElement = targetElement || placementRef.current;
        const updateInitialCoordinates = (e) => {
            const { clientX: x, clientY: y } = e.changedTouches ? e.changedTouches[0] : e;
            initialCoordinatesRef.current = { x, y };
        };
        // run on next frame so placementRef has a chance to mount, otherwise it's undefined
        const frame = requestAnimationFrame(() => {
            targetDomElement = targetElement || placementRef.current;
            if (process.env.NODE_ENV === "test" || !shouldFollowCursor || !targetDomElement) {
                return;
            }
            targetDomElement.addEventListener(initialEvent, updateInitialCoordinates);
            targetDomElement.addEventListener(initialEvent, updateInitialCoordinates);
        });
        return () => {
            cancelAnimationFrame(frame);
            targetDomElement?.removeEventListener(initialEvent, updateInitialCoordinates);
        };
    }, [initialEvent, placementRef, shouldFollowCursor, targetElement]);
    // Instantiate popper instance when both trigger and overlay are mounted
    useEffect(() => {
        const targetDomElement = targetElement || placementRef.current;
        if (process.env.NODE_ENV === "test" || !isOpen || !positionerRef.current || !targetDomElement) {
            return;
        }
        const positioner = positionerRef.current;
        const target = shouldFollowCursor ? createVirtualElement(initialCoordinatesRef) : targetDomElement;
        // give extra space for fat fingers when touch
        const defaultOffset = (hasArrow ? ARROW_SIZE : 0) + (isTouch && shouldFollowCursor ? 40 : 4);
        const offset = overlayOffset === undefined ? defaultOffset : overlayOffset;
        const modifiers = [
            {
                name: "offset",
                options: {
                    offset: [0, offset]
                }
            },
            {
                name: "arrow",
                options: {
                    // https://popper.js.org/docs/v2/modifiers/arrow/#padding
                    padding: { left: 18, top: 10, bottom: 10, right: 18 }
                }
            },
            {
                name: "flip",
                options: {
                    fallbackPlacements: fallbackPlacements ||
                        (shouldFollowCursor && isTouch ? ["top", "left", "right", "bottom"] : ["right", "left", "top", "bottom"])
                }
            },
            {
                name: "computeStyles",
                options: {
                    // DES-3860 Prevent blurry rendering on Chrome with low DPI screens
                    // default roundOffsetsByDPR doesn't work correctly at devicePixelRatio values like 1.25
                    roundOffsets: ({ x, y }) => ({
                        x: x === undefined ? 0 : Math.round(x),
                        y: y === undefined ? 0 : Math.round(y)
                    }),
                    // Disable adaptive if transitioning between targetElement locations
                    adaptive: !targetElement
                }
            },
            {
                name: "roundPopperOffsets",
                enabled: true,
                phase: "afterRead", // This should run after the dimensions have been read
                fn: (({ state }) => {
                    // DES-3860 Prevent blurry rendering for bottom placement on Chrome with low DPI screens
                    // in case popper height or width is not integer, round it
                    Object.entries(state.rects.popper).forEach(([key, value]) => {
                        state.rects.popper[key] = Math.round(value);
                    });
                })
            }
        ];
        popperRef.current =
            popperRef.current ||
                createPopper(target, positioner, {
                    placement,
                    modifiers
                });
        const instance = popperRef.current;
        let destroyVirtualElement;
        if (shouldFollowCursor && targetDomElement) {
            destroyVirtualElement = registerVirtualInstance({
                virtualElement: target,
                targetElement: targetDomElement,
                instance,
                isTouch,
                initialCoordinatesRef
            });
        }
        // Update when positioning may change due to style or className changes
        const mutationObserver = new MutationObserver(instance.update);
        mutationObserver.observe(targetDomElement, {
            subtree: true,
            attributes: true,
            attributeFilter: ["style", "class"]
        });
        return () => {
            mutationObserver.disconnect();
            instance.destroy();
            destroyVirtualElement && destroyVirtualElement();
            popperRef.current = null;
        };
    }, [
        fallbackPlacements,
        hasArrow,
        isOpen,
        isTouch,
        overlayOffset,
        placement,
        placementRef,
        positionerRef,
        shouldFollowCursor,
        targetElement
    ]);
    const overlayElement = overlayRef.current;
    const popperInstance = popperRef.current;
    useEffect(() => {
        if (!popperInstance || !overlayElement) {
            return;
        }
        // Reposition if overlay dimensions change
        const resizeObserver = new ResizeObserver(popperInstance.update);
        resizeObserver.observe(overlayElement);
        let frame = 0;
        const delayedUpdate = () => {
            frame = requestAnimationFrame(() => {
                popperInstance.update();
            });
        };
        // Handles edge case where clicking a nested popper may change the position of a parent
        // Example: SlateRTE image alignment submenu
        overlayElement.addEventListener("click", delayedUpdate);
        return () => {
            overlayElement.removeEventListener("click", delayedUpdate);
            cancelAnimationFrame(frame);
            resizeObserver.disconnect();
        };
    }, [popperInstance, overlayElement]);
    return popperRef;
};
